import { useState, useEffect, useRef } from "react";
import { 
  AlertTriangle, X, Phone, Clock, Bell, 
  MessageSquare, CheckCheck, Volume2
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function FatimaAlertsWidget({ currentUser = null, autoCheck = true }) {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);
  const audioRef = useRef(null);
  const previousAlertsRef = useRef([]);

  // Déterminer si l'utilisateur est admin/manager (voit tout) ou télépro (voit que ses alertes)
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  
  // Pour un télépro, on match son nom via email ou telepro_name
  const getUserTelepro = () => {
    if (!currentUser) return null;
    if (currentUser.telepro_name) return currentUser.telepro_name;
    // Essayer de matcher par prénom dans l'email (ex: justine@sani-adapt.fr -> Justine)
    if (currentUser.email) {
      const emailName = currentUser.email.split('@')[0];
      // Capitaliser la première lettre
      return emailName.charAt(0).toUpperCase() + emailName.slice(1).toLowerCase();
    }
    return null;
  };

  const userTelepro = getUserTelepro();

  useEffect(() => {
    loadAlerts();
    
    if (autoCheck) {
      // Vérifier toutes les 30 secondes
      const checkInterval = setInterval(() => {
        checkForNewAlerts();
      }, 30 * 1000);
      
      // Charger les alertes toutes les 10 secondes
      const loadInterval = setInterval(() => {
        loadAlerts();
      }, 10 * 1000);
      
      return () => {
        clearInterval(checkInterval);
        clearInterval(loadInterval);
      };
    }
  }, [currentUser, autoCheck]);

  const loadAlerts = async () => {
    try {
      // Si c'est un télépro (pas admin), filtrer par son nom
      let url = `${API}/fatima/alerts?unread_only=true`;
      if (!isAdmin && userTelepro) {
        url += `&telepro=${encodeURIComponent(userTelepro)}`;
      }
      
      const resp = await authFetch(url);
      if (resp.ok) {
        const data = await resp.json();
        
        // Vérifier s'il y a de nouvelles alertes
        const newAlerts = data.alerts || [];
        if (newAlerts.length > previousAlertsRef.current.length && previousAlertsRef.current.length > 0) {
          // Jouer un son de notification
          playNotificationSound();
        }
        previousAlertsRef.current = newAlerts;
        
        setAlerts(newAlerts);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Erreur chargement alertes Fatima:", error);
    }
  };

  const checkForNewAlerts = async () => {
    setIsChecking(true);
    try {
      const resp = await authFetch(`${API}/fatima/check-alerts`);
      if (resp.ok) {
        const data = await resp.json();
        setLastCheck(data.checked_at);
        
        if (data.alerts_generated > 0) {
          // Recharger les alertes si de nouvelles ont été générées
          await loadAlerts();
          playNotificationSound();
        }
      }
    } catch (error) {
      console.error("Erreur vérification alertes:", error);
    }
    setIsChecking(false);
  };

  const playNotificationSound = () => {
    try {
      // Créer un son de notification simple
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
      }, 200);
    } catch (e) {
      console.log("Impossible de jouer le son");
    }
  };

  const markAsRead = async () => {
    try {
      let url = `${API}/fatima/alerts/mark-read`;
      if (!isAdmin && userTelepro) {
        url += `?telepro=${encodeURIComponent(userTelepro)}`;
      }
      
      await authFetch(url, { method: 'POST' });
      setAlerts([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Erreur marquage alertes:", error);
    }
  };

  const handleCallBack = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber.replace(/\s/g, '')}`, '_blank');
    }
  };

  // Pas d'alertes = pas d'affichage
  if (alerts.length === 0) {
    return null;
  }

  return (
    <div 
      data-testid="fatima-alerts-widget"
      className="fixed bottom-20 right-4 z-50 w-96 max-h-[500px] overflow-hidden"
    >
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-red-600 to-orange-600 rounded-t-xl p-3 cursor-pointer"
        onClick={() => setShowAlerts(!showAlerts)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
              🎯
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">
                {isAdmin ? "Alertes Fatima (Admin)" : "Message de Fatima"}
              </h3>
              <p className="text-xs text-white/70">
                {unreadCount} message(s) {!isAdmin && userTelepro ? `pour ${userTelepro}` : "non lu(s)"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); markAsRead(); }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Marquer comme lu"
            >
              <CheckCheck className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAlerts(!showAlerts); }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Contenu */}
      {showAlerts && (
        <div className="bg-slate-900 border border-red-500/30 border-t-0 rounded-b-xl max-h-[400px] overflow-y-auto">
          {alerts.map((alert, idx) => (
            <div 
              key={idx}
              className="p-3 border-b border-slate-700/50 last:border-0"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-white text-sm">
                      {isAdmin ? alert.telepro : "Pour toi"}
                    </span>
                    <span className="text-xs text-slate-500">
                      {alert.created_at ? new Date(alert.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {alert.message}
                  </p>
                </div>
              </div>
              
              {/* Numéros à rappeler */}
              {alert.calls && alert.calls.length > 0 && (
                <div className="ml-6 mt-2 space-y-1">
                  {alert.calls.slice(0, 3).map((call, callIdx) => (
                    <div 
                      key={callIdx}
                      className="flex items-center justify-between bg-slate-800/50 rounded px-2 py-1"
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{call.time}</span>
                        <span className="text-white">{call.from_number}</span>
                      </div>
                      <button
                        onClick={() => handleCallBack(call.from_number)}
                        className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-500 text-white rounded text-xs transition-colors"
                      >
                        <Phone className="w-3 h-3" />
                        Rappeler
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* Footer avec dernière vérification */}
          <div className="p-2 bg-slate-800/50 text-center">
            <span className="text-xs text-slate-500">
              {isChecking ? "Vérification en cours..." : `Dernière vérif: ${lastCheck || '--:--'}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
